<template>
  <div class="abherov2">
    <!-- collecting: source anchor + Variant A (pill/canvas) + Variant B canvas + footer.
         (POR-13765) -->
    <template v-if="state === 'collecting'">
      <div
        class="abherov2__source"
        :class="{ 'abherov2__source--disabled': disabled }"
      >
        <div class="abherov2__srcthumb">
          <img
            v-if="sourceImage && !sourceThumbError"
            :src="sourceImage"
            :data-fullsrc="sourceFull"
            class="abherov2__srcimg"
            alt="Source"
            @error="sourceThumbError = true"
          >
          <i
            v-else-if="sourceResolving"
            class="bi bi-arrow-repeat abherov2__srcspin"
            title="Fetching the product image…"
          />
          <i
            v-else
            class="bi bi-image abherov2__srcthumb-fallback"
            title="Source product image"
          />
        </div>
        <div class="abherov2__srcbody">
          <div class="abherov2__label">
            Source
          </div>
          <textarea
            v-model="sourceRow.text"
            rows="1"
            class="abherov2__text"
            placeholder="ASIN, paste, upload, or drop an image"
            :disabled="disabled"
            @paste="onPaste(sourceIdx, $event)"
            @input="onSourceText($event)"
            @keydown.enter.prevent="$event.target.blur()"
          />
        </div>
        <div class="abherov2__icons">
          <button
            v-if="hasText(sourceRow) || sourceRow.image"
            type="button"
            class="abherov2__clear"
            :disabled="disabled"
            title="Clear"
            @click="clearContent(sourceIdx)"
          >
            &times;
          </button>
          <button
            type="button"
            class="abherov2__icon"
            :disabled="disabled"
            title="Upload from device"
            @click="pick(sourceIdx, 'upload')"
          >
            <svg
              class="abherov2__icon-svg"
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="m16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551" />
            </svg>
          </button>
          <button
            type="button"
            class="abherov2__icon"
            :disabled="disabled"
            title="Pick from file browser or Salsify"
            @click="pick(sourceIdx, 'browser')"
          >
            <svg
              class="abherov2__icon-svg"
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
          <button
            type="button"
            class="abherov2__icon"
            :disabled="disabled"
            title="Save or reuse a prompt"
            @click="promptClipboard(sourceIdx)"
          >
            <svg
              class="abherov2__icon-svg"
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <rect
                width="8"
                height="4"
                x="8"
                y="2"
                rx="1"
                ry="1"
              />
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
            </svg>
          </button>
        </div>
      </div>

      <div class="abherov2__canvases">
        <!-- Variant A: "Same as source" pill, or a describe canvas when expanded -->
        <button
          v-if="variantARow.mode === 'original'"
          type="button"
          class="abherov2__pill"
          :disabled="disabled"
          title="Modify Variant A instead of using source as-is"
          @click="setVariantMode('variantA', 'text')"
        >
          <span class="abherov2__label">Variant A</span>
          <img
            v-if="sourceImage"
            :src="sourceImage"
            alt=""
            class="abherov2__pillimg"
          >
          <div class="abherov2__pilltext">
            Same as source
          </div>
          <div class="abherov2__pillhint">
            Click to modify →
          </div>
        </button>
        <div
          v-else
          class="abherov2__canvas"
        >
          <div class="abherov2__canvashead">
            <span class="abherov2__label">Variant A</span>
            <button
              type="button"
              class="abherov2__useorig"
              :disabled="disabled"
              @click="setVariantMode('variantA', 'original')"
            >
              Use source as-is
            </button>
          </div>
          <div class="abherov2__canvasbody">
            <img
              v-if="variantARow.image && thumbSrc(variantARow) && !thumbErrors[variantARow.key]"
              :src="thumbSrc(variantARow)"
              :data-fullsrc="fullSrc(variantARow)"
              class="abherov2__thumb"
              alt=""
              @error="$set(thumbErrors, variantARow.key, true)"
            >
            <i
              v-else-if="variantARow.image && thumbErrors[variantARow.key]"
              class="bi bi-image abherov2__thumb abherov2__thumb--fallback"
              title="Selected image (preview unavailable)"
            />
            <textarea
              v-model="variantARow.text"
              rows="1"
              class="abherov2__text"
              placeholder="ASIN, paste image, or describe the variation…"
              :disabled="disabled"
              @paste="onPaste(variantAIdx, $event)"
              @input="onText($event)"
            />
            <div class="abherov2__icons">
              <button
                v-if="hasText(variantARow) || variantARow.image"
                type="button"
                class="abherov2__clear"
                :disabled="disabled"
                title="Clear"
                @click="clearContent(variantAIdx)"
              >
                &times;
              </button>
              <button
                type="button"
                class="abherov2__icon"
                :disabled="disabled"
                title="Upload from device"
                @click="pick(variantAIdx, 'upload')"
              >
                <svg
                  class="abherov2__icon-svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="m16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551" />
                </svg>
              </button>
              <button
                type="button"
                class="abherov2__icon"
                :disabled="disabled"
                title="Pick from file browser or Salsify"
                @click="pick(variantAIdx, 'browser')"
              >
                <svg
                  class="abherov2__icon-svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
              <button
                type="button"
                class="abherov2__icon"
                :disabled="disabled"
                title="Save or reuse a prompt"
                @click="promptClipboard(variantAIdx)"
              >
                <svg
                  class="abherov2__icon-svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <rect
                    width="8"
                    height="4"
                    x="8"
                    y="2"
                    rx="1"
                    ry="1"
                  />
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                </svg>
              </button>
            </div>
          </div>
          <div
            v-if="!disabled && !hasText(variantARow) && samplesFor(variantARow).length"
            class="abherov2__samplewrap"
          >
            <button
              v-for="(ex, exIdx) in samplesFor(variantARow)"
              :key="exIdx"
              type="button"
              class="abherov2__sample"
              :disabled="disabled"
              :title="ex.text"
              @click="applyChip(variantAIdx, ex.text)"
            >
              {{ ex.label }}
            </button>
          </div>
        </div>

        <!-- Variant B: always a describe canvas -->
        <div class="abherov2__canvas">
          <div class="abherov2__canvashead">
            <span class="abherov2__label">Variant B</span>
          </div>
          <div class="abherov2__canvasbody">
            <img
              v-if="variantBRow.image && thumbSrc(variantBRow) && !thumbErrors[variantBRow.key]"
              :src="thumbSrc(variantBRow)"
              :data-fullsrc="fullSrc(variantBRow)"
              class="abherov2__thumb"
              alt=""
              @error="$set(thumbErrors, variantBRow.key, true)"
            >
            <i
              v-else-if="variantBRow.image && thumbErrors[variantBRow.key]"
              class="bi bi-image abherov2__thumb abherov2__thumb--fallback"
              title="Selected image (preview unavailable)"
            />
            <textarea
              v-model="variantBRow.text"
              rows="1"
              class="abherov2__text"
              placeholder="ASIN, paste image, or describe the variation…"
              :disabled="disabled"
              @paste="onPaste(variantBIdx, $event)"
              @input="onText($event)"
            />
            <div class="abherov2__icons">
              <button
                v-if="hasText(variantBRow) || variantBRow.image"
                type="button"
                class="abherov2__clear"
                :disabled="disabled"
                title="Clear"
                @click="clearContent(variantBIdx)"
              >
                &times;
              </button>
              <button
                type="button"
                class="abherov2__icon"
                :disabled="disabled"
                title="Upload from device"
                @click="pick(variantBIdx, 'upload')"
              >
                <svg
                  class="abherov2__icon-svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="m16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551" />
                </svg>
              </button>
              <button
                type="button"
                class="abherov2__icon"
                :disabled="disabled"
                title="Pick from file browser or Salsify"
                @click="pick(variantBIdx, 'browser')"
              >
                <svg
                  class="abherov2__icon-svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
              <button
                type="button"
                class="abherov2__icon"
                :disabled="disabled"
                title="Save or reuse a prompt"
                @click="promptClipboard(variantBIdx)"
              >
                <svg
                  class="abherov2__icon-svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <rect
                    width="8"
                    height="4"
                    x="8"
                    y="2"
                    rx="1"
                    ry="1"
                  />
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                </svg>
              </button>
            </div>
          </div>
          <div
            v-if="!disabled && !hasText(variantBRow) && samplesFor(variantBRow).length"
            class="abherov2__samplewrap"
          >
            <button
              v-for="(ex, exIdx) in samplesFor(variantBRow)"
              :key="exIdx"
              type="button"
              class="abherov2__sample"
              :disabled="disabled"
              :title="ex.text"
              @click="applyChip(variantBIdx, ex.text)"
            >
              {{ ex.label }}
            </button>
          </div>
        </div>
      </div>

      <div class="abherov2__footer">
        <label class="abherov2__size">
          <span class="abherov2__size-label">Image size</span>
          <select
            v-model="localSize"
            class="abherov2__size-select"
            :disabled="disabled"
            title="Output size — PDP images are square (1:1)"
            @change="emitFormState"
          >
            <option
              v-for="opt in sizeOptions"
              :key="opt"
              :value="opt"
            >
              {{ opt.replace('x', ' × ') }}
            </option>
          </select>
        </label>
        <button
          type="button"
          class="abherov2__generate"
          :disabled="!canGenerate || disabled"
          @click="generate"
        >
          <span v-if="!generating">Generate <i class="bi bi-arrow-right" /></span>
          <span v-else>Generating <span class="abherov2__dots">···</span></span>
        </button>
      </div>
    </template>
    <!-- generating / ready: unified result card — tile stage, prompt editor, history
         strip, regenerate row and footer, ported from the legacy
         AIChatWidgetAbHeroResult.vue and restyled to sit inside this card instead of
         a separate appended message. Tile-level v-if chains (generating/done/error/
         queued) already cover BOTH the 'generating' and 'ready' top-level states,
         exactly as they did as a standalone widget — so one block renders both. (POR-13765) -->
    <template v-else>
      <div class="abherov2__cards">
        <div
          v-for="tile in tiles"
          :key="tile.rowIndex"
          class="abherov2__card"
        >
          <div class="abherov2__variant">
            {{ tile.label }}
          </div>
          <div
            class="abherov2__stage"
            @mouseenter="hovered = tile.rowIndex"
            @mouseleave="hovered = null"
          >
            <!-- Busy: regenerating OR saving — keep the previous image, blurred, with an overlay -->
            <template v-if="(tile.state === 'generating' && tile.url) || (tile.state === 'done' && tile.saving && tile.url)">
              <img
                :src="tile.url"
                class="abherov2__resultimg abherov2__resultimg--regen"
                :class="{ 'abherov2__resultimg--contain': tile.mode === 'original' }"
                :alt="tile.label"
              >
              <div class="abherov2__regen">
                <span class="abherov2__regen-pill">{{ tile.saving ? 'Saving' : 'Regenerating' }} <span class="abherov2__dots">···</span></span>
              </div>
            </template>
            <!-- Done: image + hover actions (download / save) -->
            <template v-else-if="tile.state === 'done' && tile.url">
              <img
                :src="tile.url"
                class="abherov2__resultimg"
                :class="{ 'abherov2__resultimg--contain': tile.mode === 'original' }"
                :alt="tile.label"
              >
              <div
                v-show="hovered === tile.rowIndex"
                class="abherov2__imgactions"
              >
                <button
                  type="button"
                  class="abherov2__iconbtn"
                  title="Download"
                  @click="download(tile.rowIndex)"
                >
                  <i class="bi bi-download" />
                </button>
                <button
                  type="button"
                  class="abherov2__iconbtn"
                  title="Save to File Browser"
                  @click="save(tile.rowIndex)"
                >
                  <i class="bi bi-folder-plus" />
                </button>
              </div>
            </template>
            <!-- Error -->
            <div
              v-else-if="tile.state === 'error'"
              class="abherov2__resulterror"
            >
              <i class="bi bi-exclamation-triangle abherov2__resulterror-icon" />
              <span class="abherov2__resulterror-text">Couldn't generate</span>
              <button
                type="button"
                class="abherov2__resultretry"
                :disabled="anyGenerating"
                :title="anyGenerating ? 'Wait for the other variant to finish generating' : 'Retry'"
                @click="regen(tile.rowIndex)"
              >
                <i class="bi bi-arrow-clockwise" /> Retry
              </button>
            </div>
            <!-- Initial generation -->
            <div
              v-else
              class="abherov2__resultplaceholder"
            >
              <i class="bi bi-stars abherov2__resultspark" />
              <button
                v-if="tile.slow"
                type="button"
                class="abherov2__resultretry abherov2__resultretry--slow"
                @click="regen(tile.rowIndex)"
              >
                <i class="bi bi-arrow-clockwise" /> Taking a while — Retry
              </button>
            </div>
          </div>
          <!-- Prompt editor: stays mounted below the stage through the WHOLE lifecycle of a
               variant — queued, generating and done — so the prompt is visible under the
               image while it generates and the card doesn't jump in height when the result
               lands (no layout shift). Pre-filled with the variant's prompt. -->
          <template v-if="showPromptEditor(tile)">
            <textarea
              :ref="`editTa-${tile.rowIndex}`"
              v-model="editPrompt[tile.rowIndex]"
              class="abherov2__captionedit"
              placeholder="Describe what to change, then click Regenerate…"
              :disabled="tile.state !== 'done'"
            />
            <div class="abherov2__cardfoot">
              <!-- Version history: all generations of this variant, newest first. A fixed
                   window of thumbnails with ‹ › paging keeps each one big enough to see no
                   matter how many exist; clicking one selects it as the current image (the
                   in-use one carries a check). Regenerate sits on its own row below. (POR-13544) -->
              <div
                v-if="versions(tile).length > 1"
                class="abherov2__history"
              >
                <span class="abherov2__history-label">Previous</span>
                <button
                  v-if="historyPaged(tile)"
                  type="button"
                  class="abherov2__historynav"
                  :disabled="tile.state !== 'done' || !canHistoryPrev(tile)"
                  title="Newer versions"
                  @click.stop="pageHistory(tile.rowIndex, -1)"
                >
                  <i class="bi bi-chevron-left" />
                </button>
                <div class="abherov2__history-strip">
                  <button
                    v-for="h in historyWindow(tile)"
                    :key="h"
                    type="button"
                    class="abherov2__historythumb"
                    :class="{ 'abherov2__historythumb--active': h === tile.url }"
                    :disabled="tile.state !== 'done'"
                    :title="h === tile.url ? 'Current version' : 'Use this version'"
                    @click.stop="select(tile.rowIndex, h)"
                  >
                    <img
                      :src="historyThumb(h)"
                      alt=""
                    >
                    <span
                      v-if="h === tile.url"
                      class="abherov2__historycheck"
                    ><i class="bi bi-check-lg" /></span>
                  </button>
                </div>
                <button
                  v-if="historyPaged(tile)"
                  type="button"
                  class="abherov2__historynav"
                  :disabled="tile.state !== 'done' || !canHistoryNext(tile)"
                  title="Older versions"
                  @click.stop="pageHistory(tile.rowIndex, 1)"
                >
                  <i class="bi bi-chevron-right" />
                </button>
              </div>
              <div class="abherov2__regenrow">
                <!-- Save or reuse this variant's prompt via the Clipboard. Opens the sidebar
                     bound to THIS regenerate box (Prompt tab pre-selected). (POR-13563-2) -->
                <button
                  type="button"
                  class="abherov2__sparkbtn"
                  :disabled="tile.state !== 'done'"
                  title="Save or reuse a prompt"
                  @click="openPromptClipboard(tile)"
                >
                  <svg
                    class="abherov2__icon-svg"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <rect
                      width="8"
                      height="4"
                      x="8"
                      y="2"
                      rx="1"
                      ry="1"
                    />
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                  </svg>
                </button>
                <button
                  type="button"
                  class="abherov2__regenbtn"
                  :disabled="tile.state !== 'done' || anyGenerating"
                  :title="anyGenerating && tile.state === 'done' ? 'Wait for the other variant to finish generating' : 'Regenerate this variant'"
                  @click="onRegenerate(tile)"
                >
                  <i class="bi bi-arrow-counterclockwise" /> Regenerate
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="abherov2__resultfooter">
        <button
          type="button"
          class="abherov2__createlayout"
          :disabled="!canCreateLayout"
          title="Create an editable layout from the generated image(s)"
          @click="createLayout"
        >
          <i class="bi bi-columns-gap" /> Create layout
        </button>
        <button
          type="button"
          class="abherov2__optix"
          :disabled="!canTest"
          @click="act"
        >
          A/B test in Optix&#x2122; <i class="bi bi-arrow-right" />
        </button>
      </div>
    </template>
  </div>
</template>

<script>
// How many history thumbnails are visible at once in the ready/generating result
// card; the rest are reached via ‹ › paging so thumbnails never shrink as more
// versions accumulate. Mirrors AIChatWidgetAbHeroResult.vue's HISTORY_WINDOW
// (duplicated intentionally — see POR-13765 task notes). (POR-13544, POR-13765)
const HISTORY_WINDOW = 3;

export default {
  name: 'AIChatWidgetAbHero',
  props: {
    rows: { type: Array, required: true },
    generating: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    // Output size, "WxH". PDP images are square (1:1); default 2000x2000. (POR-13385)
    size: { type: String, default: '2000x2000' },
    // Parent writes back a resolved image here: { rowKey, url, thumbnail, name }
    imageResult: { type: Object, default: null },
    // message._abHeroResult = { stopped, tiles }, written in place by startAbHeroGrid
    // once Generate is pressed. Absent while still collecting; present (with tiles)
    // while generating and once ready. (POR-13765)
    result: { type: Object, default: null },
    // Index of the chat message this form belongs to, so a Clipboard prompt inserted
    // from the sidebar can be routed back to the correct form/row. (POR-13563-2)
    messageIndex: { type: Number, default: null },
  },
  data() {
    return {
      localRows: (this.rows || []).map((r) => ({ ...r, image: r.image || null })),
      localSize: this.size || '2000x2000',
      sizeOptions: ['1500x1500', '2000x2000', '2500x2500', '3000x3000'],
      // Example prompts per variant — clicking one fills that row's editbox with its full
      // text. Shown only while the row is empty. A plain string is displayed verbatim; an
      // { label, text } object displays the short label (clamped to 2 lines) but inserts the
      // full text — used for long examples we don't want to show in full. Variant B offers a
      // second, richer example (style reference) as an alternative to choose from. (POR-13564)
      sampleTexts: {
        variantA: [
          'Remove unneeded text from the product image. Make the "64 oz" text much larger, but keep it on the product image. Keep entire product image visible.',
        ],
        variantB: [
          'Using the provided product image, add a rectangular vertical callout to the right of the image showing "ORANGE" and "64 OZ" in it suitable for a mobile hero image on a retailer image carousel web site.',
          {
            label: 'Remove unneeded text… Make "80 drops" larger. Use a style reference to add lemon wedges and honeycomb behind the product.',
            text: 'Remove unneeded text from the product image. Make the "80 drops" text much larger, but keep it on the product image. Keep entire product image visible. Using the provided image as a style reference, add lemon wedges behind the product to the left, and honeycomb behind the product to the right. Maintain a white background.',
          },
        ],
      },
      // rowKey -> true when its thumbnail <img> failed to load (show a fallback icon)
      thumbErrors: {},
      // The source anchor's own thumbnail failed to load → fallback icon.
      sourceThumbError: false,
      // Last ASIN we asked the parent to resolve into a Source image — dedupe guard so we
      // don't re-fetch on every keystroke. Reset when the Source is cleared. (POR-13765)
      lastResolvedAsin: '',
      // While an ASIN → Source image fetch is in flight, show a spinner in the Source thumb
      // so the user knows it's loading. Cleared when the image lands (imageResult watcher)
      // or the fallback timer fires. (POR-13765)
      sourceResolving: false,
      resolveTimer: null,
      // generating/ready result card state, ported from AIChatWidgetAbHeroResult.vue
      // (POR-13765): which tile's hover actions (download/save) are shown.
      hovered: null,
      // rowIndex -> the editable prompt text for that variant's regenerate box.
      // Seeded from each tile's description so the editbox is pre-filled and always
      // editable. (POR-13385-3, POR-13765)
      editPrompt: {},
      // rowIndex -> the index of the first visible history thumbnail (the ‹ › window
      // offset). View-only; not persisted. (POR-13544, POR-13765)
      historyOffset: {},
    };
  },
  computed: {
    // Derived from `result` (message._abHeroResult): no result yet -> the form is
    // still being collected; a result with any tile still in flight -> generating;
    // otherwise every tile has settled (done/error) -> ready. Both 'generating' and
    // 'ready' render the SAME ported result-card markup below — tile-level v-if
    // chains (generating/done/error/queued) already distinguish them per tile,
    // exactly as they did as a standalone widget. (POR-13765)
    state() {
      const res = this.result;
      if (!res || !Array.isArray(res.tiles)) return 'collecting';
      const anyBusy = res.tiles.some((t) => t.state === 'generating' || t.state === 'pending' || t.state === 'saving');
      return anyBusy ? 'generating' : 'ready';
    },
    // Tiles from `result` (message._abHeroResult.tiles), ported 1:1 from
    // AIChatWidgetAbHeroResult.vue's `grid.tiles`. (POR-13765)
    tiles() {
      return (this.result && this.result.tiles) || [];
    },
    // True while ANY variant is mid-(re)generation. Used to lock every variant's
    // Regenerate/Retry so the user can't start a second generation on top of an
    // in-flight one (see AIChatWidgetAbHeroResult.vue for the full rationale). (POR-13595)
    anyGenerating() {
      return this.tiles.some((t) => t.state === 'generating');
    },
    canTest() {
      return this.tiles.length === 2 && this.tiles.every((t) => t.state === 'done' && t.url);
    },
    canCreateLayout() {
      if (this.tiles.some((t) => t.state === 'generating')) return false;
      return this.tiles.some((t) => t.state === 'done' && t.url);
    },
    sourceIdx() {
      return this.localRows.findIndex((r) => r.key === 'source');
    },
    variantAIdx() {
      return this.localRows.findIndex((r) => r.key === 'variantA');
    },
    variantBIdx() {
      return this.localRows.findIndex((r) => r.key === 'variantB');
    },
    sourceRow() {
      return this.localRows[this.sourceIdx] || this.localRows[0];
    },
    variantARow() {
      return this.localRows[this.variantAIdx] || this.localRows[1];
    },
    variantBRow() {
      return this.localRows[this.variantBIdx] || this.localRows[2];
    },
    // The source row's own image, shown in the source anchor AND mirrored in the
    // Variant A "Same as source" pill.
    sourceImage() {
      return this.sourceRow && this.sourceRow.image ? this.thumbSrc(this.sourceRow) : '';
    },
    // Full-res original behind the source thumbnail, for the click-to-zoom lightbox.
    sourceFull() {
      return this.sourceRow && this.sourceRow.image ? this.fullSrc(this.sourceRow) : '';
    },
    canGenerate() {
      // A variant set to "Use original" needs no text/image of its own — it reuses the
      // source image as-is. (POR-13544)
      return this.localRows.every((r) => r.mode === 'original' || (r.text && r.text.trim().length > 0) || r.image);
    },
  },
  watch: {
    imageResult(val) {
      if (!val || !val.rowKey) return;
      const i = this.localRows.findIndex((r) => r.key === val.rowKey);
      if (i === -1) return;
      this.$set(this.thumbErrors, val.rowKey, false); // new image → re-attempt the thumbnail
      if (val.rowKey === 'source') {
        this.sourceThumbError = false; // re-attempt the source anchor thumb too
        this.sourceResolving = false; // the fetch landed → stop the loading spinner
        if (this.resolveTimer) { clearTimeout(this.resolveTimer); this.resolveTimer = null; }
      }
      this.$set(this.localRows, i, {
        ...this.localRows[i],
        image: { url: val.url, thumbnail: val.thumbnail || val.url, name: val.name || '' },
      });
      this.emitFormState();
    },
    // Seed/refresh each variant's regenerate-box editbox with its prompt. Only fills
    // entries that don't exist yet, so a user's in-progress edit is never clobbered by
    // a re-render. Ported from AIChatWidgetAbHeroResult.vue. (POR-13385-3, POR-13765)
    tiles: {
      immediate: true,
      handler(tiles) {
        (tiles || []).forEach((t) => {
          if (this.editPrompt[t.rowIndex] == null) {
            this.$set(this.editPrompt, t.rowIndex, t.description || '');
          }
        });
      },
    },
  },
  mounted() {
    // Size any pre-filled textareas (e.g. multi-line prompts restored on reload)
    // to fit their content so nothing is clipped. (POR-13385-3)
    this.$nextTick(() => this.growAll());
    // Receive a Clipboard prompt the user picked in the sidebar after opening it from a
    // variant's sparkle icon; the parent broadcasts it and only the matching form/row
    // applies it. (POR-13563-2)
    this.$root.$on('abhero:insert-prompt', this.onInsertPrompt);
  },
  beforeDestroy() {
    this.$root.$off('abhero:insert-prompt', this.onInsertPrompt);
    if (this.resolveTimer) clearTimeout(this.resolveTimer);
  },
  methods: {
    hasText(row) {
      return !!(row && row.text && row.text.trim());
    },
    // Normalised list of example prompts for a variant row. A plain string shows verbatim;
    // an { label, text } object shows the short label but inserts the full text on click.
    // The label renders in full (no line-clamp): a global style forces `display: flow-root`
    // on the chip, which defeats `-webkit-line-clamp` (it needs `-webkit-box`) and left a
    // half-line clipped at the bottom. Labels are kept short enough to read whole. (POR-13564, POR-13765)
    samplesFor(row) {
      const list = (row && this.sampleTexts[row.key]) || [];
      return list.map((ex) => (typeof ex === 'string'
        ? { label: ex, text: ex }
        : { label: ex.label, text: ex.text }));
    },
    // Display the picked image as a small THUMBNAIL via a same-origin URL so the
    // page's img-src CSP doesn't block it. Prefers a thumbnail-sized source
    // (row.image.thumbnail) over the full image. data/blob and same-origin URLs load
    // directly; an external URL (e.g. a file-browser item whose baked-in host isn't
    // allowed) is proxied through the same-origin /share?file= endpoint. If it still
    // fails, @error shows a fallback icon instead of the broken-image glyph. (POR-13385)
    thumbSrc(row) {
      const raw = (row && row.image && (row.image.thumbnail || row.image.url)) || '';
      if (!raw) return '';
      if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
      const origin = (typeof window !== 'undefined' && window.location.origin) || '';
      if (!origin) return raw;
      // Add a downscale+WebP to any /share URL that doesn't already request one, so EVERY
      // source renders a tiny preview — including same-origin /share links that aren't
      // pre-lightened (e.g. It'sRapid stock's 512px thumbnail link). (POR-13385-4)
      const lighten = (u) => (u.indexOf('/share') === -1 || /[?&](lightweight|scale|is_thumbnail)=/.test(u)
        ? u
        : `${u}${u.indexOf('?') === -1 ? '?' : '&'}lightweight=1&scale=256&format=webp`);
      if (raw.startsWith('/')) return lighten(origin + raw);
      try {
        const parsed = new URL(raw);
        if (parsed.origin === origin) return lighten(raw);
        // External CDN image (Salsify / Stock Pixabay / Bynder): proxy through /share AND
        // ask for a downscaled WebP so the 30px preview isn't the full-size original (~MBs).
        // The full image used for generation lives on row.image.url, untouched. (POR-13385-4)
        return `${origin}/share?file=${encodeURIComponent(raw)}&lightweight=1&scale=256&format=webp`;
      } catch (e) {
        return raw;
      }
    },
    // The ORIGINAL full-resolution image (not the thumbnail) as a same-origin URL, exposed
    // via :data-fullsrc so the global lightbox handler (NetworksPageV2) opens the real image
    // instead of the 256px preview. Mirrors thumbSrc's CSP handling but WITHOUT the
    // downscale: data/blob and same-origin URLs are used as-is; an external CDN url is
    // proxied through /share with no `lightweight`, which returns the original untouched.
    // (POR-13385-7)
    fullSrc(row) {
      const raw = (row && row.image && row.image.url) || '';
      if (!raw) return '';
      if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
      const origin = (typeof window !== 'undefined' && window.location.origin) || '';
      if (!origin) return raw;
      if (raw.startsWith('/')) return origin + raw;
      try {
        const parsed = new URL(raw);
        if (parsed.origin === origin) return raw;
        return `${origin}/share?file=${encodeURIComponent(raw)}`;
      } catch (e) {
        return raw;
      }
    },
    // Grow a textarea to fit ALL its content — no max-height/scroll — so a long example
    // shows in full, matching the source design. (POR-13564)
    autoGrow(el) {
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    },
    // Re-measure EVERY row's textarea after the DOM updates and again after the next
    // paint, so none is left short with a stray scrollbar when content/layout settle
    // a tick late. (POR-13385-3)
    growAll() {
      const grow = () => (this.$el.querySelectorAll('.abherov2__text') || []).forEach((el) => this.autoGrow(el));
      grow();
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(grow);
    },
    onText(e) {
      this.autoGrow(e && e.target);
      this.emitFormState();
    },
    // Source input: same as onText, but when the value is a complete ASIN and the source
    // has no image yet, ask the parent to fetch the product image so the thumbnail (and the
    // "Same as source" pill) fill in without waiting for Generate. (POR-13765)
    onSourceText(e) {
      this.autoGrow(e && e.target);
      this.emitFormState();
      this.maybeResolveSourceImage();
    },
    maybeResolveSourceImage() {
      const row = this.sourceRow;
      if (!row || row.image) return;
      const asin = (row.text || '').trim().toUpperCase();
      if (!/^B[A-Z0-9]{9}$/.test(asin) || asin === this.lastResolvedAsin) return;
      this.lastResolvedAsin = asin;
      this.sourceResolving = true;
      if (this.resolveTimer) clearTimeout(this.resolveTimer);
      // Fallback: stop the spinner if the fetch never resolves (no match / slow scrape),
      // so the field doesn't spin forever. The image still fills in if it arrives later.
      this.resolveTimer = setTimeout(() => { this.sourceResolving = false; }, 30000);
      this.$emit('action', `ab_hero_resolve_source:${JSON.stringify({ value: asin })}`);
    },
    applyChip(idx, text) {
      this.$set(this.localRows, idx, { ...this.localRows[idx], text });
      this.$nextTick(() => this.growAll());
      this.emitFormState();
    },
    pick(idx, source) {
      this.$emit('action', `ab_hero_pick:${JSON.stringify({ rowKey: this.localRows[idx].key, source })}`);
    },
    // Open the Clipboard sidebar bound to THIS variant's editbox: the parent pre-selects
    // the Prompt tab, seeds "Save current" with the current text, and routes any prompt
    // the user picks back into this same box. (POR-13563-2)
    promptClipboard(idx) {
      const row = this.localRows[idx];
      if (!row) return;
      this.$emit('action', `ab_hero_prompt_clipboard:${JSON.stringify({
        kind: 'ab_picker',
        messageIndex: this.messageIndex,
        key: row.key,
        text: (row.text || ''),
      })}`);
    },
    // A prompt was picked in the Clipboard sidebar for this widget → replace the
    // target box's text. Only the addressed form-row ('ab_picker') or result-tile
    // regenerate box ('ab_result') on THIS message reacts — mirrors the chat dock's
    // replace-on-insert. (POR-13563-2, POR-13765)
    onInsertPrompt(p) {
      if (!p || p.messageIndex !== this.messageIndex) return;
      if (p.kind === 'ab_picker') {
        const i = this.localRows.findIndex((r) => r.key === p.key);
        if (i === -1) return;
        this.$set(this.localRows, i, { ...this.localRows[i], text: String(p.text || '') });
        this.$nextTick(() => this.growAll());
        this.emitFormState();
        // Inserting an ASIN from the built-in Clipboard fetches its image too, same as a
        // system-clipboard paste / typing it. (POR-13765)
        if (p.key === 'source') this.maybeResolveSourceImage();
        return;
      }
      if (p.kind === 'ab_result') {
        if (!this.tiles.some((t) => t.rowIndex === p.key)) return;
        this.$set(this.editPrompt, p.key, String(p.text || ''));
      }
    },
    onPaste(idx, e) {
      const items = (e.clipboardData && e.clipboardData.items) || [];
      for (let i = 0; i < items.length; i += 1) {
        if (items[i].type && items[i].type.indexOf('image') === 0) {
          const file = items[i].getAsFile();
          if (file) {
            e.preventDefault();
            // Keep the pasted image in THIS row only — stop the event from bubbling to
            // the chat dock's document-level paste listener. (POR-13385)
            e.stopPropagation();
            // Parent uploads the blob and writes the URL back via imageResult.
            this.$emit('paste-image', { rowKey: this.localRows[idx].key, file });
            return;
          }
        }
      }
      // Text paste: re-measure after the DOM updates AND post-paint via growAll's
      // double-measure so the textarea fits the full pasted content. (POR-13385-6)
      this.$nextTick(() => this.growAll());
    },
    // Clear the variant's content (text and/or image), returning the row to its empty
    // state — which brings its example chips back, the same as deleting the text by
    // hand. (POR-13564)
    clearContent(idx) {
      if (this.localRows[idx] && this.localRows[idx].key === 'source') {
        this.lastResolvedAsin = '';
        this.sourceResolving = false;
        if (this.resolveTimer) { clearTimeout(this.resolveTimer); this.resolveTimer = null; }
      }
      this.$set(this.thumbErrors, this.localRows[idx].key, false);
      this.$set(this.localRows, idx, { ...this.localRows[idx], text: '', image: null });
      this.$nextTick(() => this.growAll());
      this.emitFormState();
    },
    // Toggle a variant row between the "Same as source" pill (mode 'original') and the
    // normal AI describe canvas (mode 'text'), clearing its own text/image either way —
    // mirrors the legacy picker's useOriginal/clearOriginal pair. (POR-13765)
    setVariantMode(key, mode) {
      const idx = this.localRows.findIndex((r) => r.key === key);
      if (idx === -1) return;
      this.sourceThumbError = false;
      this.$set(this.thumbErrors, this.localRows[idx].key, false);
      this.$set(this.localRows, idx, { ...this.localRows[idx], mode, text: '', image: null });
      if (mode !== 'original') this.$nextTick(() => this.growAll());
      this.emitFormState();
    },
    emitFormState() {
      this.$emit('action', `ab_hero_form_state:${JSON.stringify({ rows: this.localRows, size: this.localSize })}`);
    },
    generate() {
      if (!this.canGenerate) return;
      this.$emit('action', `ab_hero_generate:${JSON.stringify({ rows: this.localRows, size: this.localSize })}`);
    },

    // --- generating / ready result card (ported from AIChatWidgetAbHeroResult.vue,
    // restyled to sit inside this unified card — see POR-13765 task notes for why
    // this duplication is intentional). Emits the SAME ab_hero_* action strings the
    // legacy result widget did, byte-identical, so the standalone controller's
    // handlers keep working unchanged. ---

    // Whether to render the prompt editor (textarea + regenerate row) below the stage.
    // Shown from the moment a variant is queued/generating — not just once done — so
    // the prompt is visible under the image while it generates and the card keeps a
    // stable height (no layout shift when the result lands). (POR-13595)
    showPromptEditor(tile) {
      return tile.state === 'done' || tile.state === 'generating' || tile.state === 'queued';
    },
    // Open the Clipboard sidebar bound to THIS variant's regenerate box: the parent
    // pre-selects the Prompt tab, seeds "Save current" with the current text, and
    // routes any prompt the user picks back into this same box. (POR-13563-2)
    openPromptClipboard(tile) {
      const ri = tile.rowIndex;
      const raw = this.editPrompt[ri] != null ? this.editPrompt[ri] : (tile.description || '');
      this.$emit('action', `ab_hero_prompt_clipboard:${JSON.stringify({
        kind: 'ab_result',
        messageIndex: this.messageIndex,
        key: ri,
        text: String(raw || ''),
      })}`);
    },
    act() {
      if (!this.canTest) return;
      const urls = this.tiles.map((t) => t.url);
      this.$emit('action', `ab_hero_optix:${JSON.stringify({ urls })}`);
    },
    // Retry path used by the error / "taking a while" states — re-runs the variant
    // in place with its current prompt. (POR-13385-2)
    regen(rowIndex) {
      this.$emit('action', `ab_hero_retry:${JSON.stringify({ rowIndex })}`);
    },
    // Single-click Regenerate: the editbox is already editable and pre-filled, so a
    // click just fires the regeneration with whatever prompt the box currently holds.
    // The store flips the tile to "Regenerating…" synchronously. (POR-13385-3)
    onRegenerate(tile) {
      const ri = tile.rowIndex;
      const raw = this.editPrompt[ri] != null ? this.editPrompt[ri] : (tile.description || '');
      const prompt = String(raw).trim();
      this.$emit('action', `ab_hero_retry:${JSON.stringify({ rowIndex: ri, prompt })}`);
    },
    // All versions of a variant, newest first, current included so it can carry the
    // in-use check. (POR-13544)
    versions(tile) {
      const url = tile && tile.url;
      const hist = (tile && Array.isArray(tile.history)) ? tile.history.filter(Boolean) : [];
      const list = (url && !hist.includes(url)) ? [url, ...hist] : hist;
      return [...new Set(list)];
    },
    historyPaged(tile) {
      return this.versions(tile).length > HISTORY_WINDOW;
    },
    // Clamped window offset for a tile (so it stays valid if history shrank/grew). (POR-13544)
    historyOffsetFor(tile) {
      const max = Math.max(0, this.versions(tile).length - HISTORY_WINDOW);
      const raw = this.historyOffset[tile.rowIndex] || 0;
      return Math.min(Math.max(0, raw), max);
    },
    historyWindow(tile) {
      const off = this.historyOffsetFor(tile);
      return this.versions(tile).slice(off, off + HISTORY_WINDOW);
    },
    canHistoryPrev(tile) {
      return this.historyOffsetFor(tile) > 0;
    },
    canHistoryNext(tile) {
      return this.historyOffsetFor(tile) + HISTORY_WINDOW < this.versions(tile).length;
    },
    pageHistory(rowIndex, dir) {
      const tile = this.tiles.find((t) => t.rowIndex === rowIndex);
      if (!tile) return;
      const max = Math.max(0, this.versions(tile).length - HISTORY_WINDOW);
      const next = Math.min(Math.max(0, this.historyOffsetFor(tile) + dir), max);
      this.$set(this.historyOffset, rowIndex, next);
    },
    // Small same-origin thumbnail for a history version. A generated variant url is
    // usually a /share link, so ask it for a downscaled WebP rather than rendering the
    // full-size image at thumbnail size. Other url shapes (data/blob/storage) pass
    // through. (POR-13544)
    historyThumb(url) {
      if (!url || typeof url !== 'string') return url;
      if (url.startsWith('data:') || url.startsWith('blob:')) return url;
      if (url.indexOf('/share') === -1 || /[?&](lightweight|scale|is_thumbnail)=/.test(url)) return url;
      return `${url}${url.indexOf('?') === -1 ? '?' : '&'}lightweight=1&scale=256&format=webp`;
    },
    // Select a history version as the current variant image (a pure pointer move in
    // the store — no regeneration, no reordering of the strip). (POR-13544)
    select(rowIndex, url) {
      if (!url) return;
      this.$emit('action', `ab_hero_select:${JSON.stringify({ rowIndex, url })}`);
    },
    download(rowIndex) {
      this.$emit('action', `ab_hero_download:${JSON.stringify({ rowIndex })}`);
    },
    save(rowIndex) {
      this.$emit('action', `ab_hero_save:${JSON.stringify({ rowIndex })}`);
    },
    createLayout() {
      if (!this.canCreateLayout) return;
      this.$emit('action', 'ab_hero_create_layout');
    },
  },
};
</script>

<style scoped>
.abherov2 { background: transparent; padding: 0; }
.abherov2__label { font-weight: 600; color: #51635d; font-size: 13px; }

/* Source anchor */
.abherov2__source { display: flex; align-items: flex-start; gap: 12px; padding: 10px 12px; border: 1px solid #cdd6d3; border-radius: 8px; background: #fff; }
.abherov2__source--disabled { opacity: .6; }
.abherov2__srcthumb { flex: 0 0 120px; width: 120px; height: 120px; border-radius: 8px; overflow: hidden; background: #f7f9f8; border: 1px solid #e3e9e7; display: flex; align-items: center; justify-content: center; }
.abherov2__srcimg { width: 100%; height: 100%; object-fit: cover; }
.abherov2__srcthumb-fallback { color: #8a97a0; font-size: 40px; }
.abherov2__srcspin { display: inline-block; font-size: 34px; color: #8a97a0; animation: abherov2-spin 0.9s linear infinite; }
.abherov2__srcbody { flex: 1; display: flex; flex-direction: column; gap: 4px; padding-top: 2px; }

/* Shared text input styling for source + canvases */
.abherov2__text { flex: 1; border: none; outline: none; font-size: 13px; background: transparent; resize: none; overflow: hidden; line-height: 1.4; min-height: 18px; font-family: inherit; padding: 0; display: block; width: 100%; }
.abherov2__icons { display: inline-flex; align-items: center; gap: 2px; flex: 0 0 auto; opacity: 0; transition: opacity .12s ease; }
/* Icons stay quiet until you hover (or focus) the Source card / a variant canvas. (POR-13765) */
.abherov2__source:hover .abherov2__icons,
.abherov2__source:focus-within .abherov2__icons,
.abherov2__canvas:hover .abherov2__icons,
.abherov2__canvas:focus-within .abherov2__icons { opacity: 1; }
.abherov2__icon { background: none; border: none; color: #8a97a0; cursor: pointer; padding: 2px; font-size: 15px; }
.abherov2__icon-svg { display: block; }
.abherov2__icon:hover:not(:disabled) { color: #1f8a6d; }
.abherov2__clear { background: none; border: none; color: #8a97a0; cursor: pointer; font-size: 16px; line-height: 1; padding: 2px; }
.abherov2__clear:hover:not(:disabled) { color: #1f8a6d; }

/* Two variant canvases side by side */
.abherov2__canvases { display: flex; gap: 12px; padding: 12px 0; }
.abherov2__canvas { flex: 1; min-width: 0; min-height: 288px; border: 1px solid #cdd6d3; border-radius: 8px; padding: 10px; background: #fff; }
.abherov2__canvashead { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.abherov2__useorig { background: none; border: none; color: #1f8a6d; cursor: pointer; font-size: 12px; font-weight: 600; padding: 0; }
.abherov2__useorig:hover:not(:disabled) { text-decoration: underline; }
.abherov2__canvasbody { display: flex; align-items: flex-start; gap: 6px; }
.abherov2__thumb { width: 30px; height: 30px; object-fit: cover; border-radius: 6px; flex: 0 0 30px; border: 1px solid #e3e9e7; }
.abherov2__thumb--fallback { display: inline-flex; align-items: center; justify-content: center; background: #eef2f1; color: #8a97a0; font-size: 15px; }

/* "Same as source" pill replacing Variant A's canvas */
.abherov2__pill { flex: 1; min-width: 0; min-height: 288px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; border: 1px dashed #b7c4bf; border-radius: 8px; padding: 14px 10px; background: #f7f9f8; cursor: pointer; text-align: center; }
.abherov2__pill:hover:not(:disabled) { border-color: #9aa5a1; background: #eef2f1; }
.abherov2__pillimg { width: 160px; height: 160px; max-width: 100%; object-fit: cover; border-radius: 8px; border: 1px solid #e3e9e7; }
.abherov2__pilltext { font-size: 13px; font-weight: 600; color: #2b3a36; }
.abherov2__pillhint { font-size: 11px; color: #8a97a0; }

/* Sample-prompt boxes */
.abherov2__samplewrap { margin-top: 8px; display: flex; flex-direction: column; gap: 8px; }
.abherov2__sample { display: block; width: 100%; text-align: left; background: #f7f9f8; border: 1px dashed #b7c4bf; color: #51635d; border-radius: 8px; padding: 8px 11px; font-size: 12px; line-height: 1.4; cursor: pointer; font-family: inherit; }
.abherov2__sample:hover:not(:disabled) { border-color: #9aa5a1; color: #2b3a36; background: #eef2f1; }

/* Footer */
.abherov2__footer { display: flex; justify-content: space-between; align-items: center; margin-top: 4px; gap: 12px; }
.abherov2__size { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; color: #51635d; }
.abherov2__size-label { font-weight: 600; }
.abherov2__size-select { border: 1px solid #cdd6d3; border-radius: 8px; padding: 6px 8px; font-size: 13px; color: #2b3a36; background: #fff; cursor: pointer; }
.abherov2__size-select:disabled { opacity: .6; cursor: default; }
.abherov2__generate { background: #1f8a6d; border: none; color: #fff; border-radius: 8px; padding: 8px 18px; font-size: 14px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
.abherov2__generate:disabled { background: #a7c9bd; cursor: default; }

/* generating / ready result card — ported from AIChatWidgetAbHeroResult.vue
   (abres__*), renamed to abherov2__* to sit inside this unified card. (POR-13765) */
.abherov2__cards { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; align-items: stretch; margin-top: 4px; }
/* Flex column so the stage stays a fixed square and the caption wraps BELOW it
   without ever changing the image size — both variants render identical even when
   one caption is much longer than the other. (POR-13385) */
.abherov2__card { display: flex; flex-direction: column; background: #fff; border: 1px solid #e3e9e7; border-radius: 10px; padding: 10px; }
.abherov2__variant { font-size: 12px; font-weight: 600; color: #2b3a36; margin-bottom: 6px; }
/* flex: 0 0 auto + aspect-ratio:1 → the stage is always a 1:1 square of the card
   width and never stretches/shrinks to the card height, so both stages match. */
.abherov2__stage { position: relative; width: 100%; aspect-ratio: 1; flex: 0 0 auto; background: #dfe8e5; border-radius: 8px; overflow: hidden; display: flex; align-items: center; justify-content: center; }
.abherov2__resultimg { width: 100%; height: 100%; object-fit: cover; }
/* "Use original" tiles show the raw source packshot, which is often non-square — fit
   the WHOLE product inside the square stage instead of cropping it. (POR-13544) */
.abherov2__resultimg--contain { object-fit: contain; background: #fff; }
.abherov2__resultimg--regen { filter: blur(4px) brightness(.97); }
.abherov2__regen { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
.abherov2__regen-pill { background: rgba(255, 255, 255, .85); color: #2b3a36; font-size: 13px; font-weight: 600; padding: 5px 12px; border-radius: 8px; }
/* Download / Save icons over the image, shown on hover (mirrors the PDP grid). */
.abherov2__imgactions { position: absolute; right: 8px; bottom: 8px; display: flex; gap: 6px; }
.abherov2__iconbtn { width: 30px; height: 30px; border-radius: 8px; background: rgba(255, 255, 255, .95); border: 1px solid #e3e9e7; color: #2b3a36; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; font-size: 15px; box-shadow: 0 1px 4px rgba(0, 0, 0, .12); }
.abherov2__iconbtn:hover { color: #1f8a6d; border-color: #1f8a6d; }
.abherov2__resultplaceholder { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 8px; }
/* inline-block so the rotate transform applies — an inline <i> would not spin (POR-13385) */
.abherov2__resultplaceholder .abherov2__resultspark { display: inline-block; font-size: 26px; color: #1f8a6d; animation: abherov2-spin 1.4s linear infinite; }
.abherov2__resultretry--slow { background: rgba(255, 255, 255, .92); font-size: 12px; }
.abherov2__resulterror { display: flex; flex-direction: column; align-items: center; gap: 6px; color: #6b7c77; font-size: 13px; text-align: center; padding: 8px; }
.abherov2__resulterror-icon { font-size: 22px; color: #b08a3e; }
.abherov2__resultretry { background: #fff; border: 1px solid #cdd6d3; color: #1f8a6d; border-radius: 8px; padding: 5px 14px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; }
.abherov2__resultretry:hover:not(:disabled) { border-color: #1f8a6d; }
.abherov2__resultretry:disabled { opacity: .5; cursor: default; }
/* Always-visible prompt editor. flex:1 makes both columns' editboxes fill the same
   remaining height (cards are equal-height via the grid's align-items:stretch and the
   stage above is a fixed square), so the Regenerate buttons line up at the same Y in
   both columns and long text scrolls inside the box instead of breaking the card. (POR-13385-3) */
.abherov2__captionedit { flex: 1 1 auto; width: 100%; box-sizing: border-box; margin-top: 10px; min-height: 84px; font-size: 12px; color: #2b3a36; line-height: 1.45; border: 1px solid #cdd6d3; border-radius: 8px; padding: 10px 12px; resize: vertical; overflow-y: auto; font-family: inherit; outline: none; background: #fff; }
.abherov2__captionedit:focus { border-color: #1f8a6d; box-shadow: 0 0 0 2px rgba(31, 138, 109, .15); }
/* While (re)generating the box is disabled: fade it to 40% and drop the focus ring so the
   pre-filled prompt reads as visible-but-not-editable under the generating image. (POR-13595) */
.abherov2__captionedit:disabled { background: #f7f9f8; color: #51635d; cursor: default; opacity: .4; box-shadow: none; }
/* History row on top (label + ‹ › paged window of thumbnails), Regenerate on its own
   row below — per QA mock. (POR-13544) */
.abherov2__cardfoot { margin-top: 10px; display: flex; flex-direction: column; gap: 8px; }
.abherov2__history { display: flex; align-items: center; gap: 6px; min-width: 0; }
.abherov2__history-label { font-size: 11px; font-weight: 600; color: #9aa5a1; flex: 0 0 auto; }
.abherov2__history-strip { display: flex; align-items: center; gap: 5px; }
/* ‹ › paging buttons; a fixed window keeps thumbnails full-size no matter how many. */
.abherov2__historynav { flex: 0 0 auto; width: 20px; height: 28px; padding: 0; border: 1px solid #cdd6d3; border-radius: 6px; background: #fff; color: #51635d; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; }
.abherov2__historynav:hover:not(:disabled) { border-color: #1f8a6d; color: #1f8a6d; }
.abherov2__historynav:disabled { opacity: .4; cursor: default; }
/* Sparkles on the left, Regenerate on the right (POR-13563-2). */
.abherov2__regenrow { display: flex; justify-content: space-between; align-items: center; }
/* Save/reuse-prompt sparkle: quiet icon button, matching the picker's icon colour + hover. */
.abherov2__sparkbtn { background: none; border: none; color: #8a97a0; cursor: pointer; padding: 4px; display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; }
.abherov2__sparkbtn:hover:not(:disabled) { color: #1f8a6d; }
.abherov2__sparkbtn:disabled { opacity: .5; cursor: default; }
.abherov2__historythumb { position: relative; width: 40px; height: 40px; flex: 0 0 auto; padding: 0; border: 1px solid #cdd6d3; border-radius: 6px; overflow: hidden; background: #fff; cursor: pointer; line-height: 0; }
.abherov2__historythumb:hover:not(:disabled) { border-color: #1f8a6d; }
.abherov2__historythumb:disabled { opacity: .5; cursor: default; }
.abherov2__historythumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
/* The in-use version: green ring + a corner check, so it's clear which one is selected. */
.abherov2__historythumb--active { border-color: #1f8a6d; box-shadow: inset 0 0 0 2px #1f8a6d; }
.abherov2__historycheck { position: absolute; top: 2px; right: 2px; width: 14px; height: 14px; border-radius: 50%; background: #1f8a6d; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 9px; line-height: 1; }
/* Default: white outline. Hover: fill green (per QA mock). (POR-13385-3) */
.abherov2__regenbtn { background: #fff; border: 1px solid #cdd6d3; color: #2b3a36; border-radius: 8px; padding: 6px 14px; font-size: 12px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; transition: background .12s ease, border-color .12s ease, color .12s ease; }
.abherov2__regenbtn:hover:not(:disabled) { background: #1f8a6d; border-color: #1f8a6d; color: #fff; }
.abherov2__regenbtn:disabled { opacity: .5; cursor: default; }
.abherov2__resultfooter { display: flex; justify-content: flex-end; align-items: center; gap: 6px; margin-top: 12px; }
.abherov2__createlayout { background: transparent; border: none; color: #9aa5a1; border-radius: 8px; padding: 8px 12px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
.abherov2__createlayout:hover:not(:disabled) { color: #51635d; }
.abherov2__createlayout:disabled { opacity: .45; cursor: default; }
.abherov2__optix { background: #1f8a6d; border: none; color: #fff; border-radius: 8px; padding: 8px 18px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
.abherov2__optix:disabled { background: #a7c9bd; cursor: default; }
@keyframes abherov2-spin { to { transform: rotate(360deg); } }
</style>
